import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Phone, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useSiteContent } from "@/contexts/SiteContentContext";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_prenota-stretch/artifacts/kjxcti1z_cms_image_desktop_20251015161300_OHewocbWQQ.webp";

export default function StoreSelection() {
    const { t } = useSiteContent();
    const [stores, setStores] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filters, setFilters] = useState({ countries: [], regions: [] });
    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedRegion, setSelectedRegion] = useState("");

    // Persist UTM params from URL into sessionStorage so they survive navigation
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const captured = {
            utm_source: params.get("utm_source") || "",
            utm_medium: params.get("utm_medium") || "",
            utm_campaign: params.get("utm_campaign") || "",
            utm_content: params.get("utm_content") || "",
            utm_term: params.get("utm_term") || "",
        };
        if (Object.values(captured).some(Boolean)) {
            sessionStorage.setItem("magni_utm", JSON.stringify(captured));
        }
    }, []);

    const utmQs = useMemo(() => {
        try {
            const stored = sessionStorage.getItem("magni_utm");
            if (!stored) return "";
            const u = JSON.parse(stored);
            const sp = new URLSearchParams();
            Object.entries(u).forEach(([k, v]) => v && sp.set(k, v));
            const qs = sp.toString();
            return qs ? `?${qs}` : "";
        } catch (_) {
            return "";
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [storesRes, filtersRes, productsRes] = await Promise.all([
                    api.get("/stores"),
                    api.get("/stores/filters").catch(() => ({ data: { countries: [], regions: [] } })),
                    api.get("/products").catch(() => ({ data: [] })),
                ]);
                setStores(storesRes.data);
                setFilters(filtersRes.data || { countries: [], regions: [] });
                setProducts(productsRes.data || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const productById = useMemo(() => {
        const m = {};
        products.forEach((p) => { m[p.id] = p; });
        return m;
    }, [products]);

    const filtered = useMemo(() => {
        let result = stores;
        if (selectedCountry) {
            result = result.filter((s) => (s.country || "Italia") === selectedCountry);
        }
        if (selectedRegion) {
            result = result.filter((s) => (s.region || "") === selectedRegion);
        }
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            result = result.filter(
                (s) =>
                    s.name?.toLowerCase().includes(q) ||
                    s.city?.toLowerCase().includes(q) ||
                    s.region?.toLowerCase().includes(q) ||
                    s.address?.toLowerCase().includes(q) ||
                    s.cap?.toLowerCase().includes(q),
            );
        }
        return result;
    }, [stores, query, selectedCountry, selectedRegion]);

    const showFilters = filters.countries.length > 0 || filters.regions.length > 0;

    return (
        <div className="min-h-screen flex flex-col bg-magni-cream">
            <SiteHeader />

            {/* HERO */}
            <section
                className="relative overflow-hidden border-b border-magni-light"
                data-testid="hero-section"
            >
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url('${HERO_BG}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center right",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(247,247,247,0.96) 0%, rgba(247,247,247,0.85) 35%, rgba(247,247,247,0.35) 60%, rgba(247,247,247,0) 80%)",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
                    <div className="max-w-2xl magni-fade">
                        <div className="overline mb-5" data-testid="hero-eyebrow">
                            {t("home.hero.eyebrow", "MagniStretch® Trial · Solo su prenotazione")}
                        </div>
                        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-magni-navy leading-[1.05] mb-6">
                            {t("home.hero.title", "Prova il materasso che si allunga con te,")}
                            <em className="font-serif-display italic font-light opacity-70">
                                {" "}
                                {t("home.hero.title_italic", "dove preferisci.")}
                            </em>
                        </h1>
                        <p className="text-base sm:text-lg text-magni-muted leading-relaxed max-w-xl mb-10 whitespace-pre-line">
                            {t(
                                "home.hero.subtitle",
                                "Una sessione di prova esclusiva presso un rivenditore autorizzato Magniflex. Scegli il negozio più vicino e prenota in pochi click.",
                            )}
                        </p>

                        <div
                            className="relative max-w-xl"
                            data-testid="search-container"
                        >
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-magni-muted" />
                            <input
                                data-testid="store-search-input"
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t(
                                    "home.search.placeholder",
                                    "Cerca per città, regione o nome negozio...",
                                )}
                                className="magni-input pl-12 h-14 text-base"
                                style={{ paddingLeft: "3rem" }}
                            />
                        </div>
                        {showFilters && (
                            <div className="flex flex-wrap gap-3 mt-4" data-testid="store-filters">
                                {filters.countries.length > 0 && (
                                    <select
                                        data-testid="filter-country"
                                        value={selectedCountry}
                                        onChange={(e) => setSelectedCountry(e.target.value)}
                                        className="magni-input h-12 max-w-[200px]"
                                    >
                                        <option value="">Tutte le nazioni</option>
                                        {filters.countries.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                )}
                                {filters.regions.length > 0 && (
                                    <select
                                        data-testid="filter-region"
                                        value={selectedRegion}
                                        onChange={(e) => setSelectedRegion(e.target.value)}
                                        className="magni-input h-12 max-w-[200px]"
                                    >
                                        <option value="">Tutte le regioni</option>
                                        {filters.regions.map((r) => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                )}
                                {(selectedCountry || selectedRegion) && (
                                    <button
                                        data-testid="filter-reset"
                                        onClick={() => { setSelectedCountry(""); setSelectedRegion(""); }}
                                        className="text-xs uppercase tracking-[0.1em] font-semibold text-magni-navy hover:underline"
                                    >
                                        Resetta filtri
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* STORE LIST */}
            <section className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16">
                <div className="flex items-baseline justify-between mb-8 magni-fade magni-fade-delay-1">
                    <div>
                        <div className="overline mb-2">{t("home.stores.eyebrow", "I nostri negozi")}</div>
                        <h2 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                            {t("home.stores.title", "Trova il rivenditore più vicino a te")}
                        </h2>
                    </div>
                    <div
                        className="text-sm text-magni-muted hidden sm:block"
                        data-testid="store-count"
                    >
                        {loading ? "Caricamento..." : `${filtered.length} negozi`}
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="bg-white border border-magni-light p-8 h-64 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="empty-state"
                        className="bg-white border border-magni-light p-12 text-center"
                    >
                        <h3 className="font-serif-display text-2xl text-magni-navy mb-3">
                            {t("home.stores.empty_title", "Nessun negozio trovato")}
                        </h3>
                        <p className="text-magni-muted max-w-md mx-auto">
                            {stores.length === 0
                                ? t(
                                      "home.stores.empty_initial",
                                      "Al momento non ci sono negozi disponibili. Torna più tardi o contattaci direttamente.",
                                  )
                                : t(
                                      "home.stores.empty_message",
                                      "Nessun risultato per la tua ricerca. Prova con un'altra città o regione.",
                                  )}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((s, idx) => (
                            <article
                                key={s.id}
                                data-testid={`store-card-${s.slug}`}
                                className={`group bg-white border border-magni-light p-8 flex flex-col justify-between hover:shadow-lg transition-shadow magni-fade ${
                                    idx % 3 === 1
                                        ? "magni-fade-delay-1"
                                        : idx % 3 === 2
                                          ? "magni-fade-delay-2"
                                          : ""
                                }`}
                            >
                                <div>
                                    <div className="overline mb-3">
                                        {s.region || s.city}
                                    </div>
                                    <h3 className="font-serif-display text-2xl text-magni-navy leading-tight mb-4">
                                        {s.name}
                                    </h3>
                                    <div className="space-y-2 text-sm text-magni-muted">
                                        <div className="flex items-start gap-2">
                                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-magni-navy" />
                                            <span>
                                                {s.address}
                                                <br />
                                                {s.city}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 flex-shrink-0 text-magni-navy" />
                                            <span>{s.phone}</span>
                                        </div>
                                    </div>
                                    {Array.isArray(s.product_ids) && s.product_ids.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-magni-light/60" data-testid={`store-products-${s.slug}`}>
                                            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-magni-muted mb-2">
                                                Puoi provare
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {s.product_ids
                                                    .map((pid) => productById[pid])
                                                    .filter(Boolean)
                                                    .map((p) => (
                                                        <span
                                                            key={p.id}
                                                            className="inline-block px-2 py-1 border border-magni-navy text-magni-navy text-[11px] font-semibold"
                                                        >
                                                            {p.name}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-8">
                                    <Link
                                        to={`/negozio/${s.slug}${utmQs}`}
                                        data-testid={`book-store-btn-${s.slug}`}
                                        className="magni-btn-primary w-full group-hover:bg-[var(--magni-navy-dark)]"
                                    >
                                        {t("home.card.cta", "Prenota la tua prova")}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <SiteFooter />
        </div>
    );
}
